import { EStaticPageStatus, EStaticPageViewType } from 'core/model/enums/static-pages.enum';
import {
  ICreateSectionRequest,
  ICreateStaticPageRequest,
  IEditSectionRequest,
  IEditStaticPageRequest,
  ISectionListParams,
  ISectionListResponse,
  ISectionsOrderRequest,
  IStaticPageForm,
  IStaticPageListResponse,
  IStaticPagePriceGroup,
  IStaticPagesOrderRequest,
} from 'core/model/interfaces/static-pages';
import { formatDate } from 'shared/utils/date.utils';
import { removeHtmlTags } from 'shared/utils/format-string.utils';
import { buildFormData } from 'shared/utils/object.utils';
import {
  ICreateSectionRequestDTO,
  ICreateStaticPageRequestDTO,
  IEditSectionRequestDTO,
  IEditStaticPageRequestDTO,
  ISectionListParamsDTO,
  ISectionListResponseDTO,
  ISectionsOrderRequestDTO,
  IStaticPageListResponseDTO,
  IStaticPagePriceGroupDTO,
  IStaticPageResponseDTO,
  IStaticPagesOrderRequestDTO,
} from './static-pages.dto';

export const mapSectionListDtoToDomain = ({ ...rest }: ISectionListResponseDTO): ISectionListResponse => ({
  ...rest,
});

export const mapSectionParamsDomainToDto = ({ title, page }: ISectionListParams): ISectionListParamsDTO => ({
  pageTitle: title,
  page,
});

export const mapStaticPageListDtoToDomain = ({ pages }: IStaticPageListResponseDTO): IStaticPageListResponse => ({
  pages: pages.map(({ priceGroups, createdAt, isActive, ...rest }) => ({
    content: priceGroups.map(({ name, priceLists }) => ({
      priceGroup: name,
      priceList: priceLists.map(({ name }) => name).join(' - '),
    })),
    createdAt: createdAt && formatDate(createdAt, 'DD/MM/YYYY'),
    status: isActive ? EStaticPageStatus.ENABLED : EStaticPageStatus.DISABLED,
    ...rest,
  })),
});

export const mapCreateSectionRequestDomainToDto = ({ ...rest }: ICreateSectionRequest): ICreateSectionRequestDTO => ({
  ...rest,
});

export const mapEditSectionRequestDomainToDto = ({ ...rest }: IEditSectionRequest): IEditSectionRequestDTO => ({
  ...rest,
});

export const mapCreateStaticPageRequestDomainToDto = ({
  title,
  priceListIds,
  type,
  iconImage,
  textContent,
  url,
  webImage,
  mobileImage,
  isActive,
}: ICreateStaticPageRequest): FormData => {
  const body: ICreateStaticPageRequestDTO = {
    title,
    priceListIds: priceListIds?.map(id => Number(id)),
    pageTypeId: Number(type),
    icon: iconImage?.blob,
    textContent: type === EStaticPageViewType.TEXT ? removeHtmlTags(textContent ?? '') : undefined,
    redirectUrl: type === EStaticPageViewType.LINK ? url : undefined,
    webImage: type === EStaticPageViewType.IMAGE ? webImage?.blob : undefined,
    mobileImage: type === EStaticPageViewType.IMAGE ? mobileImage?.blob : undefined,
    isActive,
  };
  return buildFormData(body);
};

export const mapEditStaticPageRequestDomainToDto = ({
  title,
  priceListIds,
  type,
  iconImage,
  textContent,
  url,
  webImage,
  mobileImage,
  isActive,
}: IEditStaticPageRequest): FormData => {
  const body: IEditStaticPageRequestDTO = {
    title,
    priceListIds: priceListIds?.map(id => Number(id)),
    pageTypeId: Number(type),
    icon: iconImage?.blob,
    textContent: removeHtmlTags(textContent ?? ''),
    redirectUrl: url,
    webImage: webImage?.blob,
    mobileImage: mobileImage?.blob,
    isActive,
  };
  return buildFormData(body);
};

export const mapStaticPageResponseDtoToDomain = ({
  id,
  title,
  isActive,
  iconUrl,
  pageTypeId,
  textContent,
  webImageUrl,
  mobileImageUrl,
  redirectUrl,
  priceGroups,
}: IStaticPageResponseDTO): IStaticPageForm => ({
  id,
  title,
  priceListIds: priceGroups.flatMap(({ priceLists }) => priceLists.map(({ id }) => String(id))),
  groupListIds: priceGroups.map(({ externalId }) => externalId),
  type: String(pageTypeId) as EStaticPageViewType,
  iconImage: { url: iconUrl },
  textContent,
  url: redirectUrl,
  webImage: { url: webImageUrl },
  mobileImage: { url: mobileImageUrl },
  isActive,
});

export const mapStaticPagePriceGroupResponseDtoToDomain = ({
  priceGroups,
}: IStaticPagePriceGroupDTO): IStaticPagePriceGroup => ({
  priceGroups: priceGroups.map(({ name, externalId, priceLists }) => ({
    label: name,
    value: externalId,
    options: priceLists.map(({ id, name }) => ({ label: name, value: String(id) })),
  })),
});

export const mapSectionsOrderRequestDtoToDomain = ({ ...rest }: ISectionsOrderRequest): ISectionsOrderRequestDTO => ({
  ...rest,
});

export const mapStaticPagesOrderRequestDtoToDomain = ({
  pageIds,
}: IStaticPagesOrderRequest): IStaticPagesOrderRequestDTO => ({
  pageIds,
});
